import React, { useState, useEffect, useCallback } from 'react';

import {
  deleteAmenity,
  createAmenity,
  fetchAmenities,
  updateAmenityStatus,
} from '../../http/amenityAPI';

import MuiAlert from '@mui/material/Alert';
import {
  Card,
  Button,
  Snackbar,
  TextField,
  Typography,
  CardContent,
  CardActions,
  CircularProgress,
} from '@mui/material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />;
});

const AmenityList = ({ clinicId }) => {
  const [amenities, setAmenities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAmenities(clinicId);
      setAmenities(data);
      setError('');
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    loadData();
  }, [clinicId, loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Название обязательно для заполнения');
      return;
    }

    try {
      const amenity = await createAmenity({ ...formData, clinicId });
      setFormData({ name: '', description: '', isActive: true });
      setAmenities((prevAmenities) =>
        [...prevAmenities, amenity]
      );
      setSuccess('Напиток успешно добавлен');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Ошибка при добавлении напитка');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await updateAmenityStatus(id, !currentStatus);
      setAmenities((prevAmenities) =>
        prevAmenities.map((amenity) =>
          amenity.id === id
            ? { ...amenity, isActive: !currentStatus }
            : amenity
        )
      );
    } catch (err) {
      setError('Ошибка при изменении статуса');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот напиток?')) return;

    try {
      await deleteAmenity(id);
      setAmenities((prevAmenities) =>
        prevAmenities.filter((amenity) =>
          amenity.id !== id
        )
      )
      setSuccess('Напиток успешно удалён');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Ошибка при удалении напитка');
    }
  };

  return (
    <Card
      className='amenity-management'
      elevation={0}
      style={{ maxWidth: '600px', margin: '0', padding: '20px', width: '100%' }}
    >
      {error && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ marginTop: '70px' }} // Учитываем высоту шапки
        >
          <Alert onClose={() => setError('')} severity='error'>
            {error}
          </Alert>
        </Snackbar>
      )}
      {success && (
        <Snackbar
          open={true}
          autoHideDuration={3000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ marginTop: '70px' }} // Учитываем высоту шапки
        >
          <Alert onClose={() => setSuccess(false)} severity='success'>
            {success}
          </Alert>
        </Snackbar>
      )}

      <Typography variant='h6' sx={{ mb: 2 }}>
        Напиток перед приёмом
      </Typography>
      <Typography sx={{ mb: 3 }} color="text.secondary">
        Возможность предложить пациенту напиток перед приемом.
        <br />
        Время отправки уведомления настраивается в разделе «Рассылка по событиям» → «Предложение напитков перед приёмом».
      </Typography>

      <form onSubmit={handleSubmit} className='amenity-form' style={{ marginBottom: '20px' }}>
        <TextField
          label='Название напитка'
          value={formData.name}
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value });
            setError('');
          }}
          fullWidth
          margin='normal'
          variant='outlined'
          required
        />
        <TextField
          label='Описание напитка'
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          margin='normal'
          variant='outlined'
          multiline
          rows={3}
        />
        <Button type='submit' variant='contained' color='primary' disabled={loading} size='small'>
          {loading ? <CircularProgress size={24} /> : 'Добавить напиток'}
        </Button>
      </form>

      {loading ? (
        <CircularProgress />
      ) : (
        <div className='amenities-grid'>
          {amenities.map((amenity) => (
            <Card
              key={amenity.id}
              className={`amenity-card ${!amenity.isActive ? 'inactive' : ''}`}
              variant='outlined'
              style={{ marginBottom: '12px', width: '100%' }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='subtitle1'>{amenity.name}</Typography>
                    <Typography color='textSecondary' variant='body2'>
                      {amenity.isActive ? 'Активна' : 'Неактивна'}
                    </Typography>
                    {amenity.description && (
                      <Typography variant='body2'>{amenity.description}</Typography>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      onClick={() => handleToggleStatus(amenity.id, amenity.isActive)}
                      variant='outlined'
                      color={amenity.isActive ? 'inherit' : 'success'}
                      size='small'
                      disabled={loading}
                      sx={amenity.isActive ? { color: 'text.secondary', borderColor: 'divider' } : undefined}
                    >
                      {amenity.isActive ? 'Деактивировать' : 'Активировать'}
                    </Button>
                    <Button
                      onClick={() => handleDelete(amenity.id)}
                      variant='outlined'
                      color='error'
                      size='small'
                      disabled={loading}
                    >
                      Удалить
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
};

export default AmenityList;
